import * as v from "@badrap/valita";
import { applyVat } from "#core/utils/currency.ts";
import { $Percentage, $PositiveInteger, $ZonedDateTime } from "./misc.ts";
import { $PaymentMethodId } from "./payment-method.ts";
import { $ProductId } from "./product.ts";

// As specified in BOI-TVA-DECLA-30-10-30 I-C § 50
// <https://bofip.impots.gouv.fr/bofip/10691-PGP.html/identifiant=BOI-TVA-DECLA-30-10-30-20250416>
//
// >
// > - le numéro du justificatif ;
// > - la date (année-mois-jour-heure-minute) ;
// > - le numéro de la caisse ;
// > - le montant total toute taxe comprise ;
// > - le détail des articles ou prestations (libellé, quantité, prix unitaire, total hors taxe de la ligne, taux de TVA associé) ;
// > - toutes les données liées à la réception (immédiate ou attendue) du paiement en contrepartie (mode de règlement notamment) ;
// > - les traces de modifications et corrections apportées aux transactions enregistrées.
export const $Transaction = v
  .object({
    id: v.string(),
    datetime: $ZonedDateTime,
    till: v.literal(0),
    balanceAfterVat: $PositiveInteger,
    items: v.array(
      v
        .object({
          id: $ProductId,
          name: v.string(),
          qty: $PositiveInteger,
          priceBeforeVat: v.object({
            unit: $PositiveInteger,
            total: $PositiveInteger,
          }),
          vat: $Percentage,
        })
        .chain((item) => {
          const expectedPriceBeforeVatTotal =
            item.priceBeforeVat.unit * item.qty;

          if (expectedPriceBeforeVatTotal !== item.priceBeforeVat.total) {
            return v.err({
              message: `does not match unit price and quantity, got: ${item.priceBeforeVat.total}, expected: ${expectedPriceBeforeVatTotal}`,
              path: ["priceBeforeVat", "total"] satisfies [
                keyof typeof item,
                keyof typeof item.priceBeforeVat,
              ],
            });
          }

          return v.ok(item);
        }),
    ),
    payments: v.array(
      v.object({ method: $PaymentMethodId, amount: $PositiveInteger }),
    ),
  })
  .chain(
    // Validate items total matches expected balance
    (transaction) => {
      const expectedBalanceAfterVat = transaction.items.reduce(
        (sum, item) =>
          sum + item.qty * applyVat(item.priceBeforeVat.unit, item.vat, "EUR"),
        0,
      );

      if (transaction.balanceAfterVat !== expectedBalanceAfterVat) {
        return v.err({
          message: `expected: ${expectedBalanceAfterVat}, got: ${transaction.balanceAfterVat}`,
          path: ["balanceAfterVat"] satisfies [keyof typeof transaction],
        });
      }

      return v.ok(transaction);
    },
  )
  .chain(
    // Validate payments sum equals balance_after_vat
    (transaction) => {
      const totalPayments = transaction.payments.reduce(
        (sum, payment) => sum + payment.amount,
        0,
      );

      if (totalPayments !== transaction.balanceAfterVat) {
        return v.err({
          message: `must sum to: ${transaction.balanceAfterVat})`,
          path: ["payments"] satisfies [keyof typeof transaction],
        });
      }

      return v.ok(transaction);
    },
  );

export type Transaction = v.Infer<typeof $Transaction>;

export const $TransactionNew = v.object({
  items: v
    .array(v.object({ id: $ProductId, qty: $PositiveInteger }))
    .chain((items) =>
      items.length > 0 ? v.ok(items) : v.err("cannot be empty"),
    ),
  payments: v
    .array(v.object({ method: $PaymentMethodId, amount: $PositiveInteger }))
    .chain((payments) =>
      payments.length > 0 ? v.ok(payments) : v.err("cannot be empty"),
    ),
});

export type TransactionNew = v.Infer<typeof $TransactionNew>;
